import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PictureAsPdfOutlined, VisibilityOutlined, Note } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services";
import MainText from "../../Components/MainText";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import printInboundNote from "./printInboundNote";
import { addTab } from "../../tabs/tabsSlice";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

function InboundNotesPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;

  const canView = Boolean(
    isSuperuser ||
      effectivePerms.includes("inventory_inbound_view")
  );
  const canCreate = Boolean(isSuperuser || effectivePerms.includes("inventory_inbound_create"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadRows = useCallback(async () => {
    if (!branchId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await inventoryService.listInboundNotes({
        branch_id: branchId,
        status: statusFilter || undefined,
      });
      setRows(toArray(data));
    } catch {
      setRows([]);
      showSnack("No se pudieron cargar las notas de entrada.", "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, statusFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleOpenDetail = useCallback(
    async (noteId) => {
      if (!branchId || !noteId) return;
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const data = await inventoryService.getInboundNote(noteId, { branch_id: branchId });
        setSelectedNote(data || null);
      } catch {
        setSelectedNote(null);
        showSnack("No se pudo cargar el detalle de la nota.", "error");
      } finally {
        setDetailLoading(false);
      }
    },
    [branchId]
  );

  const handlePrintNote = useCallback(
    async (noteId) => {
      if (!branchId || !noteId) return;
      setPrintingId(noteId);
      try {
        const data = await inventoryService.getInboundNote(noteId, { branch_id: branchId });
        if (!data) {
          showSnack("No se encontro informacion para imprimir la nota.", "warning");
          return;
        }
        printInboundNote(data);
      } catch {
        showSnack("No se pudo generar la nota para impresion.", "error");
      } finally {
        setPrintingId(null);
      }
    },
    [branchId]
  );

  const handleCreateNote = useCallback(() => {
    dispatch(
      addTab({
        path: "/inventory/inbound/create",
        label: "Nueva nota de entrada",
        closable: true,
      })
    );
    navigate("/inventory/inbound/create");
  }, [dispatch, navigate]);

  const getStatusConfig = (status) => {
    const normalized = String(status || "").toUpperCase();

    switch (normalized) {
      case "APPROVED":
        return {
          label: "Aplicada",
          text: "#166534",
          bg: "rgba(22, 101, 52, 0.08)",
          border: "rgba(22, 101, 52, 0.14)",
        };
      case "DRAFT":
        return {
          label: "Borrador historico",
          text: "#92400e",
          bg: "rgba(146, 64, 14, 0.08)",
          border: "rgba(146, 64, 14, 0.14)",
        };
      case "CANCELLED":
      case "CANCELED":
        return {
          label: "Cancelada",
          text: "#991b1b",
          bg: "rgba(153, 27, 27, 0.08)",
          border: "rgba(153, 27, 27, 0.14)",
        };
      default:
        return {
          label: status || "-",
          text: "text.secondary",
          bg: "rgba(15, 23, 42, 0.04)",
          border: "rgba(15, 23, 42, 0.08)",
        };
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontSize: 13, color: "text.secondary", fontWeight: 500 }}>
            #{row?.id}
          </Typography>
        ),
      },
      {
        field: "reference",
        headerName: "Referencia",
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => row?.reference || "-",
        renderCell: ({ row }) => (
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" noWrap sx={{ fontSize: 13.5, fontWeight: 500, color: "text.primary" }}>
              {row?.reference || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Estado",
        width: 140,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const status = getStatusConfig(row?.status);
          return (
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: 999,
                fontSize: 12.2,
                fontWeight: 500,
                lineHeight: 1,
                color: status.text,
                bgcolor: status.bg,
                border: `1px solid ${status.border}`,
              }}
            >
              {status.label}
            </Box>
          );
        },
      },
      {
        field: "origin",
        headerName: "Origen",
        width: 130,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 999,
                bgcolor: row?.auto_generated ? "rgba(2, 132, 199, 0.08)" : "rgba(15, 23, 42, 0.04)",
                color: row?.auto_generated ? "info.main" : "text.secondary",
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              {row?.auto_generated ? "Automatica" : "Manual"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "items_count",
        headerName: "Items",
        width: 90,
        type: "number",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => toArray(row?.items).length,
      },
      {
        field: "created_at",
        headerName: "Fecha",
        width: 190,
        valueGetter: (_, row) => toDateTime(row?.created_at),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 220,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                onClick={() => handleOpenDetail(row?.id)}
                sx={{
                  width: 30,
                  height: 30,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    color: "primary.main",
                  },
                }}
              >
                <VisibilityOutlined sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exportar PDF">
              <IconButton
                size="small"
                disabled={printingId === row?.id}
                onClick={() => handlePrintNote(row?.id)}
                sx={{
                  width: 30,
                  height: 30,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: "rgba(220, 38, 38, 0.06)",
                    color: "error.main",
                  },
                }}
              >
                <PictureAsPdfOutlined sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

          </Stack>
        ),
      },
    ],
    [handleOpenDetail, handlePrintNote, printingId]
  );

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver notas de entrada.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <MainText title={"Notas de entrada"} icon={Note} subtitle={"Listado de notas"} />
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="DRAFT">Borrador historico</MenuItem>
            <MenuItem value="APPROVED">Aplicada</MenuItem>
            <MenuItem value="CANCELED">Cancelada</MenuItem>
          </TextField>
          {canCreate && (
            <Button variant="contained" onClick={handleCreateNote}>
              Nueva nota de entrada
            </Button>
          )}
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert />

      <Paper variant="outlined" sx={{ height: "calc(100vh - 280px)", minHeight: 360 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          rowHeight={56}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Paper>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de nota de entrada</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando...
            </Typography>
          ) : !selectedNote ? (
            <Typography variant="body2" color="text.secondary">
              Sin detalle disponible.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedNote.id}
              </Typography>
              <Typography variant="body2">
                <strong>Referencia:</strong> {selectedNote.reference || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong> {selectedNote.status}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {toDateTime(selectedNote.created_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Comentario:</strong> {selectedNote.comment || "-"}
              </Typography>

              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Presentacion ID</TableCell>
                      <TableCell>Producto</TableCell>
                      <TableCell>Presentacion</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Qty base</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {toArray(selectedNote.items).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_presentation_id}</TableCell>
                        <TableCell>{item.product_description || "-"}</TableCell>
                        <TableCell>{item.presentation_name || "-"}</TableCell>
                        <TableCell align="right">{item.qty_presentation}</TableCell>
                        <TableCell align="right">{item.qty_base}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default InboundNotesPage;
